import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Heart, MessageCircle, NotebookPen, Wind, Users, Shield, ArrowUpRight, Sparkles } from "lucide-react";
import LumaChat from "@/components/LumaChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EASE = [0.65, 0, 0.35, 1];
const VIEWPORT = { once: true, margin: "-80px" };
const HERO_MOTION = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1, ease: EASE } };
const BUBBLE_MOTION = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1.1, delay: 0.4, ease: EASE } };
const WAITLIST_MOTION = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 1 } };

const pillars = [
  {
    icon: MessageCircle,
    title: "Meet Luma",
    body: "An always-on, empathetic companion who listens without judgment and gently guides you.",
    tag: "AI friend",
  },
  {
    icon: NotebookPen,
    title: "Private Journaling",
    body: "A quiet space to untangle your thoughts. Yours alone, never shared.",
    tag: "Encrypted · Yours",
  },
  {
    icon: Wind,
    title: "Calming Exercises",
    body: "Guided breathing, grounding, and meditation for the heavy moments.",
    tag: "Under 5 minutes",
  },
  {
    icon: Users,
    title: "Peer Support",
    body: "Anonymous rooms where people who get it hold space for one another.",
    tag: "No real name required",
  },
];

export default function PeerPlusPage() {
  const [email, setEmail] = useState("");
  const [handle, setHandle] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/peer-plus/count`).then((r) => setCount(r.data?.count ?? 0)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/peer-plus/waitlist`, { email, handle: handle || null });
      setJoined(true);
      setCount(data.count ?? count);
      toast.success(data.status === "already_on_list" ? "You're already on the waitlist. We remember you." : "You're in. We'll open the door gently.");
      setEmail("");
      setHandle("");
    } catch {
      toast.error("Something got in the way. Try again in a moment.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-transparent border border-[#E7DFD5] px-4 py-3.5 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B]/70 transition-colors duration-300 rounded-sm";

  return (
    <main className="relative min-h-screen overflow-hidden" data-testid="peer-plus-page">
      {/* Soft ambient background — very different from the doctrine side */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FFFFFF] via-[#F5EFEB] to-[#FFFFFF]" />
        <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] bg-[#F59E0B]/10 rounded-full blur-[140px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#B45309]/10 rounded-full blur-[120px]" />
      </div>

      {/* HERO */}
      <section className="pt-40 md:pt-48 pb-24 md:pb-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <motion.div {...HERO_MOTION}>
            <div className="section-tag mb-6 flex items-center gap-2">
              <Heart size={12} className="text-[#B45309]" /> A safe, anonymous space for burnout
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[8rem] leading-[0.95] tracking-tight max-w-5xl">
              You&apos;ve been carrying <br className="hidden md:block" />
              <span className="italic text-[#B45309]">so much</span>. Rest here.
            </h1>
            <p className="mt-8 text-[#44403C] text-lg md:text-xl leading-relaxed max-w-2xl">
              <span className="text-[#B45309] font-mono text-sm tracking-[0.2em] uppercase mr-2">Tacc.love</span>
              is a gentle mental-wellness companion for burnt-out workers — self-help tools, peer support, and an AI friend named Luma. No real name required.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#luma" data-testid="peer-plus-cta-primary" className="btn-ember text-sm">
                Start anonymously
                <ArrowUpRight size={16} />
              </a>
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C]">
                <Shield size={12} /> Private · Free · No data sold
              </div>
            </div>
          </motion.div>

          {/* Luma sample bubble */}
          <motion.div
            {...BUBBLE_MOTION}
            className="mt-20 md:mt-24 flex items-start gap-4 max-w-xl"
            data-testid="luma-preview"
          >
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F59E0B]/40 to-[#B45309]/20 border border-[#F59E0B]/30 flex items-center justify-center shrink-0">
              <Sparkles size={18} className="text-[#B45309]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">Luma</div>
              <div className="bg-black/5 border border-[#E7DFD5] rounded-2xl rounded-tl-sm p-4 md:p-5">
                <p className="font-display text-lg md:text-2xl leading-[1.3] tracking-tight text-[#1C1917] break-words">
                  &ldquo;That sounds exhausting. Let&apos;s take one slow breath together.&rdquo;
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* LUMA — live gentle companion */}
      <section id="luma" className="py-24 md:py-32 bg-black/40 backdrop-blur-sm border-y border-[#E7DFD5]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <div className="section-tag mb-6 flex items-center justify-center gap-2">
              <Sparkles size={12} className="text-[#B45309]" /> Try Luma now · No sign-up
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight mb-6">
              Say hello. She&apos;s <span className="italic text-[#B45309]">listening</span>.
            </h2>
            <p className="text-[#78716C] text-base md:text-lg leading-relaxed max-w-xl mx-auto">
              Luma is anonymous. Nothing you type here is tied to your name. Take your time — one word at a time is enough.
            </p>
          </div>
          <LumaChat />
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 md:py-32 border-b border-[#E7DFD5]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="peer-plus-pillars">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={VIEWPORT}
                  transition={{ duration: 0.9, delay: i * 0.08, ease: EASE }}
                  className="p-8 md:p-10 border border-[#E7DFD5] bg-black/5 hover:border-[#F59E0B]/40 hover:bg-black/5 transition-all duration-500 rounded-sm"
                  data-testid={`pillar-${i}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <Icon size={26} className="text-[#B45309]" strokeWidth={1.4} />
                    <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#A8A29E]">/0{i + 1}</span>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl tracking-tight mb-3 leading-tight">{p.title}</h3>
                  <p className="text-[#78716C] text-base leading-relaxed mb-5">{p.body}</p>
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#B45309]">{p.tag}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section id="waitlist" className="py-28 md:py-40">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <motion.div {...WAITLIST_MOTION}>
            <div className="section-tag mb-6 flex items-center justify-center gap-2">
              <Heart size={12} className="text-[#B45309]" /> Your wellbeing deserves a soft place to land
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight mb-6">
              Be one of the first to <br className="hidden md:block" />
              <span className="italic text-[#B45309]">find calm</span>.
            </h2>
            <p className="text-[#78716C] text-base md:text-lg leading-relaxed max-w-xl mx-auto mb-10">
              We&apos;re opening the doors slowly and gently. Leave your email — we&apos;ll invite you the moment it&apos;s ready. Pick a soft handle if you don&apos;t want us to use your real name.
            </p>

            {joined ? (
              <div data-testid="peer-plus-joined" className="max-w-md mx-auto p-8 border border-[#F59E0B]/40 bg-[#F59E0B]/5 rounded-sm">
                <Sparkles size={22} className="text-[#B45309] mx-auto mb-3" strokeWidth={1.5} />
                <p className="font-display text-2xl md:text-3xl leading-tight tracking-tight mb-2">You&apos;re in.</p>
                <p className="text-[#78716C] text-sm leading-relaxed">We&apos;ll open the door gently. Watch your inbox — no spam, ever.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="max-w-md mx-auto space-y-4" data-testid="peer-plus-waitlist-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="peer-plus-email"
                  className={inputCls}
                />
                <input
                  type="text"
                  maxLength={40}
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder="Pick a soft handle (optional) — e.g. quiet_river"
                  data-testid="peer-plus-handle"
                  className={inputCls}
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="peer-plus-submit"
                  className="btn-ember text-sm w-full justify-center"
                >
                  {loading ? "Sending…" : "Save my spot"}
                  <ArrowUpRight size={16} />
                </button>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#A8A29E]">
                  Private · Free · No data sold
                </p>
              </form>
            )}

            {count != null && count > 0 && (
              <p data-testid="peer-plus-count" className="mt-10 font-mono text-[11px] tracking-[0.25em] uppercase text-[#78716C]">
                {count === 1 ? "1 person already resting here" : `${count.toLocaleString()} people already resting here`}
              </p>
            )}
          </motion.div>
        </div>
      </section>
    </main>
  );
}
